import { ref, watch } from 'vue'
// types
import type { Ref } from 'vue'
// tools
import { loadTrustpilotScript, renderTrustpilotBox } from '@tools/trustpilot'

interface TrustpilotWidgetHookReturn {
  refBoxFull: Ref<HTMLElement | null>
  refBoxShort: Ref<HTMLElement | null>
}

// The boxes live inside <ClientOnly> (so the Trustpilot bootstrap can't mutate an
// SSR node mid-hydration — that iframe-vs-<a> mismatch broke the hero). ClientOnly
// mounts them after this component's onMounted, so we init on the refs appearing.
// If the async bootstrap hasn't loaded yet, its own on-load scan picks the boxes up
// (they're already in the DOM by then), so no polling is needed.
export const useTrustpilotWidget = (): TrustpilotWidgetHookReturn => {
  const refBoxFull = ref<HTMLElement | null>(null)
  const refBoxShort = ref<HTMLElement | null>(null)

  // Load the bootstrap after hydration (idle) instead of eagerly in <head>, so its
  // execution stays out of the critical load window (TBT). Once loaded, its own
  // on-load scan renders the `.trustpilot-widget` boxes already in the DOM.
  onNuxtReady(() => {
    loadTrustpilotScript()
  })

  watch([refBoxFull, refBoxShort], ([full, short]) => {
    renderTrustpilotBox(full)
    renderTrustpilotBox(short)
  }, { flush: 'post' })

  return {
    refBoxFull,
    refBoxShort,
  }
}
